/**
 * Combat stat model. Base stats come from class/monster definitions, level and
 * gear; modifiers come from statuses (buffs/debuffs), sigils, cards and gems.
 * `deriveStats` folds everything into the numbers the combat code reads.
 */

export interface BaseStats {
  level: number;
  maxHp: number;
  maxMp: number;
  /** Attack power. */
  attack: number;
  defense: number;
  /** 0..1 */
  critRate: number;
  /** Multiplier applied on crit (2.0 = +100%). */
  critDmg: number;
  /** Metres per second. */
  moveSpeed: number;
  /** 1 = normal speed. */
  atkSpeed: number;
  /** Fraction of cooldown removed (0..0.6). */
  cdr: number;
  /** HP regenerated per second (fraction of max HP) out of combat. */
  hpRegen: number;
  /** MP per second in combat / out of combat. */
  mpRegenCombat: number;
  mpRegenIdle: number;
  /** Additional damage fraction (engravings, cards, etc.). */
  dmgBonus: number;
  /** Extra identity/awakening damage from Specialization. */
  specBonus: number;
  /** Extra damage vs targets under hard CC (Domination). */
  vsCCBonus: number;
  /** Damage reduction fraction (Endurance / buffs). */
  dmgReduction: number;
  /** Extra positional bonuses from sigils. */
  backBonus: number;
  headBonus: number;
  /** Stagger and destruction multipliers. */
  staggerMult: number;
  identityGainMult: number;
  healingReceivedMult: number;
}

export const defaultBaseStats = (): BaseStats => ({
  level: 1,
  maxHp: 1000,
  maxMp: 1000,
  attack: 100,
  defense: 100,
  critRate: 0.1,
  critDmg: 2.0,
  moveSpeed: 5,
  atkSpeed: 1,
  cdr: 0,
  hpRegen: 0.01,
  mpRegenCombat: 25,
  mpRegenIdle: 80,
  dmgBonus: 0,
  specBonus: 0,
  vsCCBonus: 0,
  dmgReduction: 0,
  backBonus: 0,
  headBonus: 0,
  staggerMult: 1,
  identityGainMult: 1,
  healingReceivedMult: 1,
});

/** Additive modifier bag contributed by statuses and passive effects. */
export interface StatMods {
  atkPct: number;
  dmgPct: number;
  /** Damage taken from all sources (+ = more damage, the "brand"/synergy debuff). */
  dmgTakenPct: number;
  /** Crit rate granted to attackers hitting this target. */
  critTakenAdd: number;
  defPct: number;
  moveSpeedPct: number;
  atkSpeedPct: number;
  critRate: number;
  critDmg: number;
  dmgReduction: number;
  mpRegenPct: number;
  cdr: number;
  staggerTakenPct: number;
  /** Casting speed for cast-bar skills (+1 = twice as fast, i.e. cast times halved). */
  castSpeedPct: number;
  /** Movement multiplier outside the speed cap (mounts). */
  moveSpeedMult: number;
}

export const emptyMods = (): StatMods => ({
  atkPct: 0,
  dmgPct: 0,
  dmgTakenPct: 0,
  critTakenAdd: 0,
  defPct: 0,
  moveSpeedPct: 0,
  atkSpeedPct: 0,
  critRate: 0,
  critDmg: 0,
  dmgReduction: 0,
  mpRegenPct: 0,
  cdr: 0,
  staggerTakenPct: 0,
  castSpeedPct: 0,
  moveSpeedMult: 0,
});

export const addMods = (into: StatMods, from: Partial<StatMods>, k = 1): StatMods => {
  for (const key of Object.keys(from) as (keyof StatMods)[]) {
    into[key] += (from[key] ?? 0) * k;
  }
  return into;
};

export interface DerivedStats extends BaseStats {
  mods: StatMods;
}

/** Speed caps mirror the reference game's +40% additional speed cap. */
export const SPEED_CAP = 0.4;

export function deriveStats(base: BaseStats, mods: StatMods): DerivedStats {
  const speedBonus = Math.min(SPEED_CAP, Math.max(-0.8, mods.moveSpeedPct));
  const atkSpeedBonus = Math.min(SPEED_CAP, Math.max(-0.8, mods.atkSpeedPct));
  return {
    ...base,
    attack: base.attack * (1 + mods.atkPct),
    defense: base.defense * Math.max(0, 1 + mods.defPct),
    critRate: Math.min(1, Math.max(0, base.critRate + mods.critRate)),
    critDmg: base.critDmg + mods.critDmg,
    moveSpeed: base.moveSpeed * (1 + speedBonus) * (1 + mods.moveSpeedMult),
    atkSpeed: base.atkSpeed * (1 + atkSpeedBonus),
    cdr: Math.min(0.6, base.cdr + mods.cdr),
    dmgBonus: base.dmgBonus + mods.dmgPct,
    dmgReduction: Math.min(0.9, base.dmgReduction + mods.dmgReduction),
    mpRegenCombat: base.mpRegenCombat * (1 + mods.mpRegenPct),
    mpRegenIdle: base.mpRegenIdle * (1 + mods.mpRegenPct),
    mods,
  };
}

/** Defence to damage multiplier: diminishing returns, 50% reduction when defence equals K. */
export const DEFENSE_K = 6500;
export function defenseMultiplier(defense: number, penetration = 0): number {
  const d = Math.max(0, defense * (1 - penetration));
  return DEFENSE_K / (DEFENSE_K + d);
}
